import { useMemo, useState } from "react";
import { ArrowUturnLeftIcon, CheckIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { Button, EmptyState, SearchableSelect, Select } from "../ui";
import { useDraft } from "../../hooks/useDraft";
import { abroadAdminApi } from "../../api/abroadLoans";
import { errorMessageOf } from "../../api/scholarships";
import { canManageScholarships } from "../scholarships/access";
import { requirementFormOf, requirementSpecOf, validateRequirements } from "../scholarships/DocumentsTab";
import { eligibilityFormOf, eligibilityRulesOf, validateEligibility } from "../scholarships/EligibilityTab";
import { criteriaRowsOf, standardRubricFor, validateCriteria } from "../scholarships/CriteriaTab";
import { programmeLabel, useCallSaved, useProgrammes } from "./model";
import { CriteriaSection, EligibilitySection, MAX_CRITERIA, MAX_DOCUMENTS, RequirementsSection, totalWeightOf } from "./OfferSections";

const NO_CHANGES = {};
const MAX_PROGRAMMES = 50;
// The catalogue is small (mostly study levels), so the picker loads it in one page
const CATALOGUE_SIZE = 100;

// An offered programme as the form edits it
const offerFormOf = (p) => ({
  programme: p.programme,
  documents: (p.requirements ?? []).map(requirementFormOf),
  criteria: criteriaRowsOf(p.criteria),
});
// A newly offered programme starts from the standard criteria for its level
const newOffer = (programme) => ({ programme, documents: [], criteria: criteriaRowsOf(standardRubricFor(programme.levelName)) });

const specOf = (o) => ({
  programmeId: o.programme.id,
  requirements: o.documents.map(requirementSpecOf),
  criteria: o.criteria.map((c) => ({ name: c.name.trim(), guideline: c.guideline.trim() || null, weight: Number(c.weight), bands: c.bands })),
});

// Who may apply belongs to a level of study: one form per level, shared by its programmes
const EMPTY_ELIGIBILITY = eligibilityFormOf([]);
const levelFormsOf = (levels = []) => Object.fromEntries(levels.map((l) => [l.levelId, eligibilityFormOf(l.eligibility)]));
const levelIdsOf = (offer) => [...new Set(offer.map((o) => o.programme.levelId))];
const levelSpecsOf = (offer, levels) => levelIdsOf(offer).map((levelId) => ({ levelId, eligibility: eligibilityRulesOf(levels[levelId] ?? EMPTY_ELIGIBILITY) }));
const sameOffer = (a, b) => JSON.stringify([a.offer.map(specOf), levelSpecsOf(a.offer, a.levels)]) === JSON.stringify([b.offer.map(specOf), levelSpecsOf(b.offer, b.levels)]);

// Each programme's errors by section (eligibility: its level's); programmes without any are left out
const validateOffer = (offer, levels) =>
  Object.fromEntries(
    offer
      .map((o, i) => {
        const criteria = validateCriteria(o.criteria);
        if (totalWeightOf(o.criteria) > 100) criteria.total = "The weights add up to more than 100";
        const errors = { documents: validateRequirements(o.documents), eligibility: validateEligibility(levels[o.programme.levelId] ?? EMPTY_ELIGIBILITY), criteria };
        return [i, errors];
      })
      .filter(([, errors]) => Object.values(errors).some((e) => Object.keys(e).length))
  );

const summaryOf = (o) => `${o.documents.length} document${o.documents.length === 1 ? "" : "s"}, weights ${totalWeightOf(o.criteria)}/100`;

/**
 * The programmes a loan call offers, each with its documents and marking criteria, and who may apply
 * to each level of study (shared by every programme of that level). The API replaces the whole offer
 * at once, so one Save sends everything; unsaved edits stay a draft per call. Only a draft call's
 * offer changes.
 */
export default function ProgrammesTab({ call }) {
  const saved = useCallSaved();
  const [draft, setDraft, clearDraft] = useDraft(`abroad-offer:${call.id}`, NO_CHANGES);
  const [selected, setSelected] = useState(0);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const editable = canManageScholarships() && call.state === "DRAFT";
  const original = useMemo(() => ({ offer: (call.programmes ?? []).map(offerFormOf), levels: levelFormsOf(call.levels) }), [call.programmes, call.levels]);
  const offer = draft.offer ?? original.offer;
  const levels = draft.levels ?? original.levels;
  const changed = !sameOffer({ offer, levels }, original);
  const index = Math.min(selected, Math.max(offer.length - 1, 0));
  const current = offer[index];
  const currentLevel = current?.programme.levelId;
  const sameLevel = offer.filter((o) => o.programme.levelId === currentLevel).length;

  // Saved rules keep their level or field even once retired
  const keep = useMemo(() => {
    const rules = (call.levels ?? []).flatMap((l) => l.eligibility ?? []);
    const of = (kind) => rules.filter((r) => r.kind === kind).map((r) => ({ id: r.value, name: r.valueName }));
    return { levels: of("CURRENT_LEVEL"), fields: of("FIELD_OF_STUDY") };
  }, [call.levels]);

  const { data: catalogue, isError: catalogueFailed } = useProgrammes({ size: CATALOGUE_SIZE });
  const addOptions = useMemo(() => {
    const offered = new Set(offer.map((o) => o.programme.id));
    return (catalogue?.items ?? []).filter((p) => !offered.has(p.id)).map((p) => ({ value: p.id, label: `${programmeLabel(p)} (${p.levelName})` }));
  }, [catalogue, offer]);

  const setState = (next) => setDraft(sameOffer(next, original) ? NO_CHANGES : next);
  const setOffer = (next) => setState({ offer: next, levels });
  const updateCurrent = (patch) => {
    setOffer(offer.map((o, i) => (i === index ? { ...o, ...patch } : o)));
    if (errors[index]) setErrors((prev) => ({ ...prev, [index]: undefined }));
  };
  // The current programme's level: every programme of that level shares the change
  const updateLevel = (eligibility) => {
    setState({ offer, levels: { ...levels, [currentLevel]: eligibility } });
    setErrors({});
  };

  const add = (programmeId) => {
    const programme = catalogue?.items.find((p) => p.id === programmeId);
    if (!programme || offer.length >= MAX_PROGRAMMES) return;
    setOffer([...offer, newOffer(programme)]);
    setSelected(offer.length);
  };
  const remove = () => {
    setOffer(offer.filter((_, i) => i !== index));
    setErrors({});
    setSelected(Math.max(index - 1, 0));
  };
  // Another programme's documents and criteria, replacing this one's; its level's rules too when the levels differ
  const copyFrom = (sourceIndex) => {
    const source = offer[Number(sourceIndex)];
    const next = offer.map((o, i) => (i === index ? { ...o, documents: source.documents.slice(0, MAX_DOCUMENTS), criteria: source.criteria.slice(0, MAX_CRITERIA) } : o));
    const sourceLevel = source.programme.levelId;
    setState({ offer: next, levels: sourceLevel === currentLevel ? levels : { ...levels, [currentLevel]: levels[sourceLevel] ?? EMPTY_ELIGIBILITY } });
    setErrors({});
    toast.success(`Copied the set-up of ${source.programme.name}`);
  };

  const discard = () => {
    clearDraft();
    setErrors({});
  };

  const save = async () => {
    const found = validateOffer(offer, levels);
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first !== undefined) {
      setSelected(Number(first));
      return toast.error(`Check ${offer[first].programme.name}`);
    }

    setSaving(true);
    try {
      const { data } = await abroadAdminApi.offer(call.id, offer.map(specOf), levelSpecsOf(offer, levels));
      saved(data);
      toast.success("Programmes saved");
      discard();
    } catch (error) {
      console.error(`Failed to save the programmes of loan call ${call.id}:`, error);
      toast.error(errorMessageOf(error, "Failed to save the programmes. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const disabled = !editable || saving;
  const programmeOptions = offer.map((o, i) => ({ value: String(i), label: `${programmeLabel(o.programme)} (${summaryOf(o)})${errors[i] ? " · check" : ""}` }));

  return (
    <div className="flex flex-col gap-6">

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        {offer.length > 0 && <Select label="Programme" value={String(index)} onChange={(e) => setSelected(Number(e.target.value))} options={programmeOptions} className="sm:flex-1" />}
        {editable && offer.length < MAX_PROGRAMMES && (
          <SearchableSelect
            label="Offer a programme"
            placeholder={catalogueFailed ? "Could not load the programmes" : "Type or select a programme"}
            value=""
            onChange={(e) => add(e.target.value)}
            options={addOptions}
            disabled={saving}
            className="sm:w-80"
          />
        )}
      </div>

      {!current ? (
        <EmptyState title="No programmes yet" description="Offer at least one programme from the catalogue: each has its own documents and criteria, and its level of study its own rules." />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{current.programme.name}</span>
            <span>{[current.programme.levelName, current.programme.institutionName].filter(Boolean).join(" · ")}</span>
            {changed && <span className="text-amber-700">Unsaved changes</span>}
            {editable && (
              <div className="ml-auto flex flex-wrap items-center gap-2">
                {offer.length > 1 && (
                  <Select
                    aria-label="Copy the set-up of"
                    placeholder="Copy the set-up of…"
                    value=""
                    onChange={(e) => copyFrom(e.target.value)}
                    options={offer.map((o, i) => ({ value: String(i), label: o.programme.name })).filter((o) => o.value !== String(index))}
                    disabled={saving}
                    className="w-56"
                  />
                )}
                <Button variant="ghost" size="sm" icon={<TrashIcon className="h-4 w-4" />} onClick={remove} disabled={saving}>
                  Remove
                </Button>
              </div>
            )}
          </div>

          <RequirementsSection documents={current.documents} onChange={(documents) => updateCurrent({ documents })} errors={errors[index]?.documents} disabled={disabled} />
          <EligibilitySection
            level={current.programme.levelName}
            shared={sameLevel}
            form={levels[currentLevel] ?? EMPTY_ELIGIBILITY}
            onChange={updateLevel}
            keep={keep}
            errors={errors[index]?.eligibility}
            disabled={disabled}
          />
          <CriteriaSection rows={current.criteria} level={current.programme.levelName} onChange={(criteria) => updateCurrent({ criteria })} errors={errors[index]?.criteria} disabled={disabled} />
        </>
      )}

      {editable && (
        <div className="flex flex-col-reverse justify-end gap-3 border-t border-slate-100 pt-5 sm:flex-row">
          <Button variant="ghost" onClick={discard} disabled={saving || !changed} icon={<ArrowUturnLeftIcon className="h-4 w-4" />}>
            Discard changes
          </Button>
          <Button onClick={save} loading={saving} disabled={!changed} icon={<CheckIcon className="h-4 w-4" />}>
            Save programmes
          </Button>
        </div>
      )}
    </div>
  );
}
